import { createHttpClient } from '@adapters/HttpClient';
import { AuthApiAdapter } from '@modules/Auth/adapters/AuthApiAdapter';
import { AuthService } from '@modules/Auth/services/AuthService';
import { useToast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services';
import { useAuthStore } from '@shared/stores/authStore';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { impersonateService } from '../services/ImpersonateService';

// 🎯 Hook para iniciar impersonación
export const useStartImpersonation = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { t } = useTranslation('clientsModule');
    const { toast } = useToast();
    const { startImpersonation, setAuthenticated } = useAuthStore();

    // Crear instancia del servicio de autenticación
    const httpClient = createHttpClient(apiClientWH);
    const apiAdapter = new AuthApiAdapter(httpClient);
    const authService = new AuthService(apiAdapter);

    return useMutation({
        mutationFn: async (userId: number) => {
            // 1. Llamar al endpoint de impersonación
            const response = await impersonateService.startImpersonation(userId);

            // 2. Obtener datos del usuario impersonado con el nuevo token
            // Temporalmente guardar el token para hacer la petición
            useAuthStore.getState().setToken(response.token);
            const userData = await authService.getCurrentUser();

            return { response, userData };
        },
        onSuccess: ({ response, userData }) => {
            // 3. Actualizar el store con los datos del usuario impersonado
            const data = userData as any;
            startImpersonation(
                response.token,
                data.user,
                data.team,
                data.role,
                response.impersonation_log_id || 0
            );
            setAuthenticated(true);

            // 4. Invalidar TODAS las queries para forzar recarga completa
            queryClient.invalidateQueries();
            queryClient.clear(); // Limpia el cache completamente

            // 5. Mostrar notificación
            toast({
                title: t('impersonation.success'),
                variant: 'success',
                duration: 3000,
            });

            // 6. Dar tiempo a que Zustand persista el estado antes de recargar
            setTimeout(() => {
                window.location.href = '/';
            }, 500);
        },
        onError: (error: any) => {
            console.error('Error al iniciar impersonación:', error);
            toast({
                title: 'Error',
                description: error?.message || 'Error al iniciar impersonación',
                variant: 'destructive',
                duration: 5000,
            });
        },
    });
};

// 🎯 Hook para detener impersonación
export const useStopImpersonation = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { t } = useTranslation('clientsModule');
    const { toast } = useToast();
    const { stopImpersonation: stopImpersonationStore, setAuthenticated } = useAuthStore();

    // Crear instancia del servicio de autenticación
    const httpClient = createHttpClient(apiClientWH);
    const apiAdapter = new AuthApiAdapter(httpClient);
    const authService = new AuthService(apiAdapter);

    return useMutation({
        mutationFn: async () => {
            // 1. Obtener el log_id del store
            const logId = useAuthStore.getState().impersonationLogId;
            if (!logId) {
                throw new Error('No active impersonation session');
            }

            // 2. Llamar al endpoint para detener impersonación
            const response = await impersonateService.stopImpersonation(logId);

            // 3. Usar el NUEVO token que devuelve el backend (no el original guardado)
            // El backend genera un token fresco al detener la impersonación
            useAuthStore.getState().setToken(response.token);
            const userData = await authService.getCurrentUser();

            return { response, userData };
        },
        onSuccess: ({ response, userData }) => {
            // 4. Actualizar el store con el NUEVO token y datos del usuario original
            const data = userData as any;

            // Limpiar primero el estado de impersonación
            stopImpersonationStore();

            // Actualizar con el nuevo token y datos del backend
            useAuthStore.getState().setToken(response.token);
            useAuthStore.getState().setUser(data.user);
            useAuthStore.getState().setTeam(data.team);
            useAuthStore.getState().setRole(data.role);
            setAuthenticated(true);

            // 5. Invalidar TODAS las queries para forzar recarga completa
            queryClient.invalidateQueries();
            queryClient.clear(); // Limpia el cache completamente

            // 6. Mostrar notificación
            toast({
                title: t('impersonation.stopped'),
                variant: 'success',
                duration: 3000,
            });

            // 7. Dar tiempo a que Zustand persista el estado antes de recargar
            setTimeout(() => {
                window.location.href = '/';
            }, 500);
        },
        onError: (error: any) => {
            console.error('Error al detener impersonación:', error);
            toast({
                title: 'Error',
                description: error?.message || 'Error al detener impersonación',
                variant: 'destructive',
                duration: 5000,
            });
        },
    });
};

// 🎯 Hook para verificar si el usuario puede impersonar
export const useCanImpersonate = () => {
    const { canAccess } = usePermissions();
    return canAccess(['tenant_3pl'], ['iam.impersonation.start']);
};

